/**
 * The sky over the learner's own town (DOM-free): light is earned, never scheduled. The town rests at night, a little lighter the more of the words heard are kept alive; the moon
 * waxes with the share of practised words that carry a lamp; a sitting's end eases the sky toward first light by the
 * share of today's round done; a town whose practice is all up to speed (the server's `day`) is drawn by day.
 * The renderers take `sky` 0 (night) to 1 (day); 0.5 is the blue hour.
 */

/** Fewest practised words for the day (mirrors `DAY_MIN` in server/src/town.ts, which decides; the client only words it). */
export const DAY_MIN = 12;
/** The farthest a sitting's dawn goes (first light, never the blue hour). */
export const DAWN_MAX = 0.42;
/** The brightest resting sky short of the day: every heard word kept alive, still below first light so lamps read. */
export const REST_MAX = 0.3;
/** How long the sky takes to ease toward the dawn, in ms (a cut under reduced motion). */
export const DAWN_MS = 3500;
/** How long it takes to close back to night, in ms. */
export const CLOSE_MS = 600;

/** The dawn a sitting earns: the share of today's round done (clamped to 0..1) of `DAWN_MAX`. */
export function dawnTarget(share: number): number {
  const s = Number.isFinite(share) ? Math.min(1, Math.max(0, share)) : 0;
  return s * DAWN_MAX;
}

/** The share of everything heard that is kept alive: lamps over every word (fog counts against it); 0 when none, clamped to 0..1. */
export function keptShare(counts: { lamps: number; scaffolding: number; wood: number; stone: number; gold: number; fog: number }): number {
  const all = counts.scaffolding + counts.wood + counts.stone + counts.gold + counts.fog;
  return all > 0 ? Math.min(1, Math.max(0, counts.lamps / all)) : 0;
}

/** The moon's share lit: words carrying a lamp over practised words (buildings out of the fog); 0 when none is practised. */
export function moonShare(counts: { lamps: number; scaffolding: number; wood: number; stone: number; gold: number }): number {
  const practised = counts.scaffolding + counts.wood + counts.stone + counts.gold;
  return practised > 0 ? Math.min(1, Math.max(0, counts.lamps / practised)) : 0;
}

/** The own town's resting sky: 1 by day (the server's `day`), else `REST_MAX` of the kept share, so night stays home. */
export function skyOf(own: { day?: boolean; counts?: Parameters<typeof keptShare>[0] } | null): number {
  if (own?.day) return 1;
  return own?.counts ? REST_MAX * keptShare(own.counts) : 0;
}

/** The sky a sitting's dawn eases to: `share` of round done, never below the resting sky `rest`, at most `DAWN_MAX`; 1 by day. */
export function dawnSky(rest: number, share: number, day: boolean): number {
  return day ? 1 : Math.min(DAWN_MAX, Math.max(rest, dawnTarget(share)));
}

/** The sky `t01` (0..1) of the way through a move from `from` to `to`, easing out (cubic). */
export function skyAt(t01: number, from: number, to: number): number {
  const x = Math.min(1, Math.max(0, t01));
  return from + (to - from) * (1 - (1 - x) ** 3);
}
